import React, { useState, useEffect } from 'react'
import loadable from '@loadable/component'
import firebase from 'gatsby-plugin-firebase'

import isBrowser from 'src/util/isBrowser'
import analytics from 'src/util/analytics'
import { useStore, actions } from 'store'

import { parse as parseQueryString } from 'query-string'

import { Layout, ErrorScreen } from 'components'

const AskForEmail = loadable(() =>
  import(/* webpackPrefetch: true */ 'src/components/Organisms/AskForEmail'),
)

const AuthProvider = ({ children }) => {
  const [{ loginEmail, auth }, dispatch] = useStore()
  const [signInLinkHandled, setSignInLinkHandled] = useState(false)
  const [signInLinkError, setSignInLinkError] = useState(false)
  const [isSignInLink, setIsSignInLink] = useState('unchecked')

  const urlContainsSignInLink = () =>
    firebase.auth().isSignInWithEmailLink(window.location.href)

  const checkForAuthLink = () => {
    const check = urlContainsSignInLink()
    setIsSignInLink(check)
    // no sign in link so we finished loading
    if (!check) {
      dispatch(actions.setSignInLinkLoaded(true))
    }
  }

  const updateSignInLinkLoaded = (value) => {
    setSignInLinkHandled(value)
    dispatch(actions.setSignInLinkLoaded(value))
  }

  useEffect(() => {
    checkForAuthLink()
    /*
      gave infinite loop!
    return history.listen(({ action }) => {
      if (action === 'PUSH') {
        checkForAuthLink()
      }
    })
      */
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const authStateChanged = async (user, updateHandled) => {
    // this function is not updated with state, so we can't use that here!

    // if we don't have a user, nobody is signed in so we don't need to load
    dispatch(actions.resetUserData({ firebaseLoaded: !user }))
    dispatch(actions.setAuthenticated({ user }))

    // we applied the signed in link, so with this change the link is handled
    if (updateHandled) {
      updateSignInLinkLoaded(true)
    }
  }

  useEffect(() => {
    // listen for auth or token changes
    const unsubscribe = firebase.auth().onAuthStateChanged(authStateChanged)
    // unsubscribe to the listener when unmounting
    return () => unsubscribe()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isSignInLink === true && !signInLinkHandled && loginEmail) {
      // the flow on the same device where they started it.
      // The client SDK will parse the code from the link for you.
      firebase
        .auth()
        .signInWithEmailLink(loginEmail, window.location.href)
        .then((result) => {
          if (result.user) {
            dispatch(actions.signedIn(result.user))
          }
          authStateChanged(result.user, true)
        })
        .catch((error) => {
          updateSignInLinkLoaded(true)
          setSignInLinkError(error.code)
        })
    }
  }, [isSignInLink, loginEmail]) // eslint-disable-line react-hooks/exhaustive-deps

  const customToken = parseQueryString(window.location.search).adminToken

  useEffect(() => {
    if (customToken) {
      firebase.auth().signInWithCustomToken(customToken)
    }
  }, [customToken])

  const uid = auth.user?.uid
  useEffect(() => {
    if (auth.isAuthenticated && uid) {
      // identify this user with segment
      analytics.identify(uid)
    }
  }, [auth.isAuthenticated, uid])

  if (isSignInLink === true && !loginEmail) {
    return <AskForEmail />
  }

  if (isSignInLink && signInLinkError) {
    return (
      <Layout>
        <ErrorScreen error={signInLinkError} />
      </Layout>
    )
  }

  return children
}

export const signOut = () => firebase.auth().signOut()

const AuthProviderWrapper = ({ children }) => {
  if (!isBrowser()) {
    return children
  }
  return <AuthProvider>{children}</AuthProvider>
}

export default AuthProviderWrapper
